import { SliderProps as MaterialSliderProps } from "@material-ui/core";
import { AnimatePresence, motion, MotionProps } from "framer-motion";
import { Box, Text } from "grommet";
import { useEffect, useMemo, useState, memo } from "react";
import { usePopper } from "react-popper";
import { useHover } from "react-use";
import { StyledSlider, SliderNumber, thumbColors } from "./styles";
import explanations from "../../task-specific/initializations/explanations_export.json";

const motionConfig = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.08 },
} as MotionProps;

export interface SliderProps extends MaterialSliderProps {
  significant: boolean;
  sliderNumber: number;
  onSliderHover(sliderNumber?: number): void;
}

function __linspace(start: number, nvalues: number, interval: number) {
  if (typeof interval === "undefined") {
    interval = 0;
  }
  let i;
  const r = [];
  for (i = 0; i < nvalues; i++) {
    r.push(start + i * interval);
  }
  return r;
}

const Slider = memo(function Slider({ significant, sliderNumber, onSliderHover, ...rest }: SliderProps): React.ReactElement {
  const [showPopper, setShowPopper] = useState<boolean>(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "top",
    modifiers: [
      // add some padding on the popper when it hits the window viewport for example
      {
        name: "preventOverflow",
        options: {
          altAxis: true,
          padding: 10,
        },
      },
      {
        name: "offset",
        options: {
          offset: [0, 20],
        },
      },
    ],
  });

  const sliderElement = (
    <Box ref={setReferenceElement as any} height="150px" style={significant ? {} : { opacity: rest.disabled ? 0.2 : 1.0 }}>
      <Text size="10px" textAlign="center" margin={{ bottom: "10px" }}>
        {rest.value ? Math.round(Number(rest.value) * 10) / 10 : 0}
      </Text>
      <StyledSlider background={thumbColors[__linspace(-4, 17, 0.5).indexOf(Math.round(Number(rest.value) * 2) / 2)]} {...rest} />
      <SliderNumber margin={{ top: "6px" }} textAlign="center">
        {sliderNumber}
      </SliderNumber>
    </Box>
  );
  /**
   * When a user hovers over a slider we want to perform a callback in order to
   * show explanations about the corresponding latent dimension.
   */
  const [hoverable, hovered] = useHover(sliderElement);
  const explanation = useMemo(() => {
    if (sliderNumber in explanations) return (explanations as any)[sliderNumber];
    return {
      title: "Insignificant dimension",
      description: "This dimension was not used by the algorithm to encode any information about the ECG.",
    };
  }, [sliderNumber]);
  useEffect(() => {
    if (hovered) {
      setShowPopper(true);
      onSliderHover(sliderNumber);
    } else {
      setShowPopper(false);
    }
  }, [hovered]);
  return (
    <>
      {hoverable}
      <AnimatePresence>
        {showPopper && (
          <motion.div
            ref={setPopperElement as any}
            style={{ ...(styles.popper as any), zIndex: 1000 }}
            {...attributes.popper}
            {...motionConfig}
          >
            <Box background="white" elevation="medium" round="8px" width="350px" pad="15px">
              <Text size="medium" weight={600}>
                {explanation.title}
              </Text>
              <Text size="small">{explanation.description}</Text>

              {explanation.title != "Insignificant dimension" && (
                <Box>
                  {explanation.low_text && (
                    <Box>
                      <Text size="small" weight={600} color="#000094" margin={{ top: "5px" }}>
                        Low values are predictive for:
                      </Text>
                      <Text size="small">{explanation.low_text}</Text>
                    </Box>
                  )}

                  {explanation.high_text && (
                    <Box>
                      <Text size="small" weight={600} color="#b20000" margin={{ top: "5px" }}>
                        High values are predictive for:
                      </Text>
                      <Text size="small">{explanation.high_text}</Text>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

export default Slider;
